import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

const initializedProductDocuments = new WeakSet();

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();

    if (this.#redirectInitialVariantSelection()) return;

    this.#cacheRadioState();
    this.#syncStyleFirstAvailability();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  #redirectInitialVariantSelection() {
    if (
      this.dataset.templateProductMatch !== 'true' ||
      this.dataset.requireExplicitSelection !== 'true' ||
      initializedProductDocuments.has(document)
    ) {
      return false;
    }

    initializedProductDocuments.add(document);

    const url = new URL(window.location.href);
    if (!url.searchParams.has('variant') && !url.searchParams.has('option_values')) return false;

    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');
    window.location.replace(url.toString());
    return true;
  }

  #cacheRadioState() {
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const radios = /** @type {HTMLInputElement[]} */ (Array.from(fieldset?.querySelectorAll('input') ?? []));
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      this.#checkedIndices.push(initialCheckedIndex === -1 ? [] : [initialCheckedIndex]);
    });
  }

  /** @returns {Array<{id: number | string, available: boolean, price: number, formattedPrice: string, options: string[], featuredMediaId: number | string | null}>} */
  #getVariantMatrix() {
    const script = this.querySelector('script[data-variant-matrix]');
    if (!script?.textContent) return [];

    try {
      const matrix = JSON.parse(script.textContent);
      return Array.isArray(matrix) ? matrix : [];
    } catch {
      return [];
    }
  }

  /** @returns {Map<number, string>} */
  #getSelectedValuesByPosition() {
    const selectedValues = new Map();

    for (const input of this.querySelectorAll('fieldset input:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const position = Number.parseInt(input.dataset.optionPosition || '', 10);
      if (!Number.isNaN(position)) selectedValues.set(position, input.value);
    }

    for (const select of this.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement) || !select.value) continue;
      const selected = select.selectedOptions[0];
      const position = Number.parseInt(selected?.dataset.optionPosition || select.dataset.optionPosition || '', 10);
      if (!Number.isNaN(position)) selectedValues.set(position, select.value);
    }

    return selectedValues;
  }

  /** @returns {{id: number | string, available: boolean, price: number, formattedPrice: string, options: string[], featuredMediaId: number | string | null} | undefined} */
  #getSelectedVariant() {
    if (!this.isSelectionComplete) return;

    const selectedValues = this.#getSelectedValuesByPosition();
    return this.#getVariantMatrix().find(
      (variant) =>
        variant.available &&
        Array.from(selectedValues).every(
          ([position, selectedValue]) => variant.options[position - 1] === selectedValue
        )
    );
  }

  /** @param {number | string} variantId */
  #syncProductVariantId(variantId) {
    const selectedVariant = this.#getSelectedVariant();
    if (!selectedVariant || String(selectedVariant.id) !== String(variantId)) return;

    for (const selectedControl of this.querySelectorAll('fieldset input:checked, select option:checked')) {
      if (selectedControl instanceof HTMLElement) selectedControl.dataset.variantId = String(variantId);
    }

    const scope = this.closest('.shopify-section, dialog');
    if (!scope) return;

    for (const productForm of scope.querySelectorAll('product-form-component')) {
      if (!(productForm instanceof HTMLElement) || productForm.dataset.productId !== this.dataset.productId) continue;

      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (!(variantIdInput instanceof HTMLInputElement)) continue;

      variantIdInput.value = String(variantId);
      variantIdInput.disabled = false;
      variantIdInput.setAttribute('value', String(variantId));
    }
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} control
   * @param {boolean} available
   * @param {boolean} disabled
   */
  #setControlAvailability(control, available, disabled) {
    control.dataset.optionAvailable = String(available);
    control.disabled = disabled;

    if (disabled) {
      control.setAttribute('aria-disabled', 'true');
    } else {
      control.removeAttribute('aria-disabled');
    }

    const baseLabel = control.dataset.optionBaseLabel || control.value;
    const unavailableLabel = this.dataset.unavailableLabel || 'Unavailable';

    if (control instanceof HTMLInputElement) {
      control.setAttribute('aria-label', available ? baseLabel : `${baseLabel} - ${unavailableLabel}`);
      control.closest('label')?.querySelector('.variant-option__strikethrough')?.toggleAttribute('hidden', available);
    } else {
      control.textContent = available ? baseLabel : `${baseLabel} - ${unavailableLabel}`;
    }
  }

  #syncStyleFirstAvailability() {
    if (this.dataset.styleFirst !== 'true') return;

    const variants = this.#getVariantMatrix();
    const stylePosition = Number.parseInt(this.dataset.styleOptionPosition || '', 10);
    if (!variants.length || Number.isNaN(stylePosition)) return;

    const selectedValues = this.#getSelectedValuesByPosition();
    const selectedStyle = selectedValues.get(stylePosition);
    const controls = this.querySelectorAll(
      'fieldset input[data-option-position], select option[data-option-position]'
    );

    for (const control of controls) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLOptionElement)) continue;

      const position = Number.parseInt(control.dataset.optionPosition || '', 10);
      if (Number.isNaN(position)) continue;

      const awaitingStyle = position !== stylePosition && !selectedStyle;
      let available = true;

      if (position === stylePosition) {
        available = variants.some(
          (variant) => variant.available && variant.options[position - 1] === control.value
        );
      } else if (!awaitingStyle) {
        available = variants.some((variant) => {
          if (!variant.available || variant.options[position - 1] !== control.value) return false;
          if (variant.options[stylePosition - 1] !== selectedStyle) return false;

          for (const [selectedPosition, selectedValue] of selectedValues) {
            if (selectedPosition === position || selectedPosition === stylePosition) continue;
            if (variant.options[selectedPosition - 1] !== selectedValue) return false;
          }

          return true;
        });
      }

      this.#setControlAvailability(control, awaitingStyle ? true : available, awaitingStyle || !available);

      if (!available && !awaitingStyle) {
        if (control instanceof HTMLInputElement && control.checked) control.checked = false;
        if (control instanceof HTMLOptionElement && control.selected) control.selected = false;
      }
    }

    for (const optionGroup of this.querySelectorAll('[data-option-role="dependent"]')) {
      optionGroup.toggleAttribute('data-awaiting-style', !selectedStyle);
    }

    this.dataset.selectionComplete = String(this.isSelectionComplete);
  }

  #clearDependentSelections() {
    for (const input of this.querySelectorAll('fieldset[data-option-role="dependent"] input:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = false;
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
    }

    for (const select of this.querySelectorAll('select[data-option-role="dependent"]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      select.value = '';
      for (const option of select.options) option.removeAttribute('selected');
    }

    this.#cacheRadioState();
  }

  #disableProductPurchase() {
    const scope = this.closest('.shopify-section, dialog');
    if (!scope) return;

    for (const productForm of scope.querySelectorAll('product-form-component')) {
      if (!(productForm instanceof HTMLElement) || productForm.dataset.productId !== this.dataset.productId) continue;

      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement) {
        variantIdInput.value = '';
        variantIdInput.disabled = true;
        variantIdInput.setAttribute('value', '');
      }

      for (const addToCart of productForm.querySelectorAll('add-to-cart-component')) {
        if ('disable' in addToCart && typeof addToCart.disable === 'function') addToCart.disable();

        const button = addToCart.querySelector('button[ref="addToCartButton"]');
        if (!(button instanceof HTMLButtonElement)) continue;

        button.disabled = true;
        const textNode = Array.from(button.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
        );
        if (textNode) textNode.textContent = ` ${this.dataset.selectOptionsLabel || 'Select options'} `;
      }

      productForm.querySelector('[ref="acceleratedCheckoutButtonContainer"]')?.setAttribute('hidden', 'true');
    }

    const sticky = document.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`);
    if (sticky instanceof HTMLElement) {
      sticky.dataset.currentVariantId = '';
      sticky.dataset.variantAvailable = 'false';
      const stickyButton = sticky.querySelector('button[ref="addToCartButton"]');
      if (stickyButton instanceof HTMLButtonElement) {
        stickyButton.disabled = true;
        const stickyLabel = stickyButton.querySelector('.add-to-cart-text__content > span:first-child');
        if (stickyLabel) stickyLabel.textContent = this.dataset.selectOptionsLabel || 'Select options';
      }

      const stickyVariant = sticky.querySelector('.sticky-add-to-cart__variant');
      if (stickyVariant) stickyVariant.textContent = '';
    }
  }

  /** @param {string} styleValue */
  #updateStylePrice(styleValue) {
    const variants = this.#getVariantMatrix().filter((variant) => {
      const stylePosition = Number.parseInt(this.dataset.styleOptionPosition || '', 10);
      return variant.available && variant.options[stylePosition - 1] === styleValue;
    });
    if (!variants.length) return;

    const prices = variants.map((variant) => variant.price);
    const minimumPrice = Math.min(...prices);
    const maximumPrice = Math.max(...prices);
    const minimumVariant = variants.find((variant) => variant.price === minimumPrice);
    if (!minimumVariant) return;

    const priceTemplate = this.dataset.priceFromTemplate || 'From __PRICE__';
    const displayPrice =
      minimumPrice === maximumPrice
        ? minimumVariant.formattedPrice
        : priceTemplate.replace('__PRICE__', minimumVariant.formattedPrice);

    const scope = this.closest('.shopify-section, dialog');
    const productPrice = Array.from(scope?.querySelectorAll('product-price') ?? []).find(
      (element) => element instanceof HTMLElement && element.dataset.productId === this.dataset.productId
    );
    const priceContainer = productPrice?.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const price = document.createElement('span');
    price.className = 'price';

    if (minimumPrice !== maximumPrice) {
      const rangeLabel = document.createElement('span');
      rangeLabel.className = 'visually-hidden';
      rangeLabel.textContent = `${this.dataset.priceRangeLabel || 'Price range'}\u00a0`;
      price.append(rangeLabel);
    }

    price.append(document.createTextNode(displayPrice));
    priceContainer.replaceChildren(price);
  }

  /** @param {string} styleValue */
  async #selectStyleMedia(styleValue) {
    const stylePosition = Number.parseInt(this.dataset.styleOptionPosition || '', 10);
    const representativeVariant = this.#getVariantMatrix().find(
      (variant) =>
        variant.available &&
        variant.featuredMediaId &&
        variant.options[stylePosition - 1] === styleValue
    );
    if (!representativeVariant) return;

    await this.#selectMediaById(representativeVariant.featuredMediaId);
  }

  /** @param {number | string | null | undefined} mediaId */
  async #selectMediaById(mediaId) {
    if (!mediaId) return;

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Scope to the closest section/dialog (mirrors #syncProductVariantId) so this targets the
    // right gallery instance - the page's own section on the PDP, or the quick-add dialog's
    // cloned gallery when running inside the quick-add modal - instead of always grabbing
    // whatever gallery happens to live in <main>.
    const scope = this.closest('.shopify-section, dialog') || document;
    const gallery = scope.querySelector('media-gallery');
    const isQuickAddGallery = Boolean(gallery?.closest('quick-add-dialog'));
    // Inside any dialog (quick-add modal) the gallery is always shown as a single-image
    // carousel (see inflyn-brand.css), regardless of the grid/carousel setting configured for
    // the main product page, so skip the grid-reorder branch there.
    const isGridPresentation =
      gallery?.dataset.presentation === 'grid' &&
      window.matchMedia('(min-width: 750px)').matches &&
      !gallery.closest('dialog');

    if (isGridPresentation) {
      const grid = gallery.querySelector('.media-gallery__grid');
      const gridMedia = grid?.querySelector(`[data-media-id="${mediaId}"]`);
      const gridItem = gridMedia?.closest('.product-media-container');
      if (!grid || !gridItem || gridItem.parentElement !== grid) return;

      const gridItems = Array.from(grid.children).filter((item) => item instanceof HTMLElement);
      gridItems.forEach((item, index) => {
        if (!item.dataset.variantPickerOriginalIndex) {
          item.dataset.variantPickerOriginalIndex = String(index);
        }
      });

      gridItems
        .sort(
          (a, b) =>
            Number(a.dataset.variantPickerOriginalIndex || 0) - Number(b.dataset.variantPickerOriginalIndex || 0)
        )
        .forEach((item) => grid.append(item));
      grid.prepend(gridItem);
      return;
    }

    const media = gallery?.querySelector(`slideshow-component[ref="slideshow"] [data-media-id="${mediaId}"]`);
    const slide = media?.closest('slideshow-slide');
    if (!slide) return;

    // Quick add replaces its media gallery with freshly fetched markup after
    // every option change. The new custom element may not have rebuilt its
    // slideshow ref yet, so update the single-image state directly instead of
    // depending on the carousel instance during that upgrade window.
    if (isQuickAddGallery) {
      const gallerySlides = gallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide');
      gallerySlides.forEach((candidate) => {
        candidate.setAttribute('aria-hidden', candidate === slide ? 'false' : 'true');
      });
      return;
    }

    const slideshow = gallery?.slideshow;
    const slides = slideshow?.slides;
    if (!slides?.length || typeof slideshow.select !== 'function') return;

    const index = slides.indexOf(slide);
    if (index === -1) return;

    await slideshow.select(index, undefined, { animate: false });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const usesStyleFirstFlow = this.dataset.styleFirst === 'true';
    const selectedStyle = usesStyleFirstFlow && selectedOption.dataset.optionRole === 'style';

    if (selectedStyle) {
      this.#clearDependentSelections();
    }
    this.#syncStyleFirstAvailability();

    const selectedVariant = this.#getSelectedVariant();
    if (selectedVariant) {
      selectedOption.dataset.variantId = String(selectedVariant.id);
      this.#syncProductVariantId(selectedVariant.id);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Media (gallery image) sync should also run inside the quick-add modal, not just the real
    // product page - the modal renders the same gallery markup and benefits from the same
    // "selecting a style swaps the photo" behavior. URL/history updates must stay page-only
    // though, so this flag is deliberately separate from isOnProductPage and only guards the
    // media-sync calls below (never the browser URL).
    const canSyncMedia = isOnProductPage || Boolean(event.target.closest('quick-add-dialog'));

    if (canSyncMedia && selectedVariant?.featuredMediaId) {
      this.#selectMediaById(selectedVariant.featuredMediaId).catch(() => {});
    }

    if (canSyncMedia && this.dataset.requireExplicitSelection === 'true' && !this.isSelectionComplete) {
      if (isOnProductPage) {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
      }

      if (usesStyleFirstFlow && this.#getSelectedValuesByPosition().has(Number(this.dataset.styleOptionPosition))) {
        this.#disableProductPurchase();

        const styleValue = this.#getSelectedValuesByPosition().get(Number(this.dataset.styleOptionPosition));
        if (!styleValue) return;

        this.#updateStylePrice(styleValue);
        this.#selectStyleMedia(styleValue).catch(() => {});
      }
      return;
    }

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    const selectionPromise = this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    if (canSyncMedia) {
      selectionPromise
        .then(({ detail }) => {
          const resolvedVariantId = detail?.resource?.id;
          if (!resolvedVariantId) return;

          const resolvedMediaId = detail.resource?.featured_media?.id;
          if (resolvedMediaId) {
            setTimeout(() => this.#selectMediaById(resolvedMediaId).catch(() => {}));
          }

          this.#syncProductVariantId(resolvedVariantId);
          setTimeout(() => this.#syncProductVariantId(resolvedVariantId));

          if (isOnProductPage) {
            const resolvedUrl = new URL(window.location.href);
            resolvedUrl.searchParams.set('variant', String(resolvedVariantId));
            if (resolvedUrl.href !== window.location.href) history.replaceState({}, '', resolvedUrl.toString());
          }
        })
        .catch(() => {});
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   * @returns {Promise<any>} The resolved variant update shared with product listeners.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });

    return deferredEventPromise.promise;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#cacheRadioState();
    this.#syncStyleFirstAvailability();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string, position: number}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({
          name: selected.dataset.optionName,
          value: selected.value,
          position: Number.parseInt(selected.dataset.optionPosition || select.dataset.optionPosition || '', 10),
        });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({
          name: input.dataset.optionName,
          value: input.value,
          position: Number.parseInt(input.dataset.optionPosition || '', 10),
        });
      }
    }

    return options
      .sort((a, b) => a.position - b.position)
      .map(({ name, value }) => ({ name, value }));
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option:checked, fieldset input:checked'))
      .filter((option) => option.dataset.optionValueId)
      .sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Whether every customer-selectable option group has an explicit value.
   * Single-value product options are rendered as labels and need no interaction.
   * @returns {boolean}
   */
  get isSelectionComplete() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    const selects = Array.from(this.querySelectorAll('select'));

    return (
      fieldsets.every((fieldset) => fieldset.querySelector('input:checked')) &&
      selects.every((select) => select.value !== '')
    );
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
